<script>
import { feature } from '@/utils/feature.helper'
import i18n from '@/i18n'
import Layout from '@/views/layouts/main'
import MultiSelect from '@/components/multi-select'
import ListingPagination from '@/components/listing-pagination'
import { inboxService } from '@/helpers/services/inbox.service'
import { tenantService } from '@/helpers/services/tenant.service'
import { inboxAlert } from '@/state/inbox-alert'
import { sessionHelper, sharedHelper, tenantHelper } from '@/utils'

/**
 * The agent desk — the screen a CS person keeps open all shift.
 *
 * ── Why it polls instead of using websockets ─────────────────────────────
 * Laravel Reverb would be the "right" answer and it costs a supervised daemon
 * to run. At the scale this starts at (tens of agents, not hundreds) a 5-second
 * poll of two indexed queries is cheaper to operate and cannot silently stop
 * working the way a dropped socket can. Revisit past ~100 concurrent agents.
 *
 * The queue and the open thread poll on separate clocks: the queue can be
 * seconds stale, a live conversation cannot.
 *
 * ── Why there is an alert at all ─────────────────────────────────────────
 * An agent with this tab in the background learns nothing from a list that
 * silently grows. Three signals, cheapest first: the tab title, a short tone,
 * and a desktop notification if they have granted it. All of them fire only on
 * a NEW waiting conversation, never on a refresh that happens to return the
 * same rows — an alert that cries wolf gets muted within a day.
 */
export default {
  // Every authenticated page renders inside the vertical layout —
  // topbar, sidebar, footer. Without this the view mounts bare at the
  // top-left of a blank page, which is what the missing wrapper looks like.
  components: { Layout, MultiSelect, ListingPagination },
  /**
   * Just the screen's name. The waiting count is NOT added here any more.
   *
   * It used to be, and that scoped the count to this one screen: an agent on
   * Users saw a plain title while three players waited. App.vue's
   * titleTemplate now prefixes `(n)` for every page from the shared alert
   * state, so there is one owner for the number and it follows the agent
   * around. Adding it here too would render `(3) (3) Inbox`.
   */
  page() {
    return { title: this.$t('module.inbox') }
  },
  data() {
    return {
      conversations: [],
      total: 0,
      /*
       * The queue is paged.
       *
       * It was not, and the consequence was quiet: the list asked for 50 rows
       * and rendered whatever came back, so on a busy brand the 51st waiting
       * player was invisible with nothing on screen saying so. The server has
       * always paginated this endpoint and returned `meta.total`; only the
       * control was missing.
       *
       * 30 a page rather than 50: the pane scrolls, and a pager you have to
       * scroll to find is a pager nobody uses.
       */
      page: 1,
      pageSize: 30,

      /*
       * What this agent may do, as the SERVER computed it.
       *
       * Not derived here from the cached permission list, because the rule is
       * not "holds the permission": an agent holding none of the conversation
       * rights keeps the full-queue default (see the backend's
       * ConversationAccess). Re-deriving that in the SPA is how you get a
       * restricted agent shown a full agent picker whose every choice 403s, or
       * an unrestricted one shown none at all.
       *
       * Permissive until the first response lands, so the controls do not
       * flicker out and back on a reload.
       */
      can: { read_all: true, assign_others: true, own_only: false, push: false },

      /** The tone sniff's vocabulary, in the order the dropdown shows it. */
      toneOptions: ['praise', 'hostile', 'lost', 'unsure', 'neutral'],
      /** "Message a member" dialog. */
      push: { show: false, tenantId: null, memberId: '', body: '', busy: false },
      /** Brands for the dialog's picker, loaded when it opens on "All brands". */
      pushBrands: [],

      /*
       * When a waiting row turns red, in seconds. Comes from the server, which
       * reads it from Configuration (`inbox_urgent_after_minutes`) — it is a
       * service promise, and it differs per desk.
       *
       * 600 until the first response lands, so the queue does not briefly
       * paint every row red (or none of them) on a slow first load.
       */
      urgentAfterSeconds: 600,

      /*
       * Opens on "All", not on "Waiting".
       *
       * Waiting was the original default on the reasoning that it is the queue
       * an agent should work. In practice it hides the commonest case: a
       * conversation the bot is handling is `bot`, not `waiting`, so a brand
       * with no handoffs opens on an empty inbox that reads as broken. The
       * waiting rows still sort to the top of All (FIELD(status,...) server
       * side), so the queue is the first thing on screen either way — and the
       * tab counter and the alert still key off waiting, not off what is
       * filtered.
       */
      filter: '',
      mine: false,
      search: '',
      searchTimer: null,

      selected: null,
      /** Filled only when an agent presses Look up. Cleared on every open. */
      member: null,
      memberLoading: false,
      /** Context pane beside the thread: the member behind the chat, and the
       *  briefs it produced. `leads` is fetched on open; the member is not. */
      ctxTab: 0,
      leads: [],
      leadsLoading: false,
      /** One Sales CRM: which brief is being pushed, and the by-hand form. */
      crmBusy: null,
      crmAdd: { open: false, busy: false, error: '', name: '', phone: '', email: '', company: '', notes: '' },
      /** What the bot remembers about this customer (brand has memory on). */
      memory: null,
      memoryEnabled: false,
      memoryLoading: false,
      messages: [],
      /**
       * The newest message this agent had seen before opening. 0 = never
       * opened, so nothing is marked new — a first visit should not claim the
       * whole history is unread.
       */
      lastReadId: 0,
      hasMore: false,
      loadingMore: false,

      agents: [],
      draft: '',
      isPrivate: false,
      sending: false,
      uploading: false,
      loadingList: false,
      loadingThread: false,
      error: '',

      listTimer: null,
      threadTimer: null,

      /**
       * The app-wide alert, for the bell toggle in this screen's header.
       *
       * Announcing is no longer this component's job — see
       * state/inbox-alert.js. Both pollers running their own announcer is how
       * one conversation got two tones.
       */
      alert: inboxAlert.state,
      /** Mobile shows one pane at a time. */
      mobileView: 'list',
    }
  },
  computed: {
    /** The brand's enabled skills, from the conversation payload (mirrored tenant row). */
    brandSkills() {
      return (this.selected && Array.isArray(this.selected.skills)) ? this.selected.skills : []
    },
    hasJk() { return this.brandSkills.includes('jk') },
    /** Per-brand switches the server already combined with the platform flags. */
    brandFeatures() { return (this.selected && this.selected.features) || {} },
    /** Proposal tab: the brand collects briefs AND turned the tab on (Organisation → Inbox). */
    hasLead() { return this.brandSkills.includes('lead') && feature('proposal') && Boolean(this.brandFeatures.proposal) },
    /** One Sales tab: the brand's bot is connected to a one_sales (ai_bot integrations.one_sales). */
    hasOneSales() { return feature('one_sales_crm') && Boolean(this.brandFeatures.one_sales) },
    /** Briefs are fetched for either tab. */
    wantsLeads() { return this.hasLead || this.hasOneSales },
    /** The member record as label/value rows — whatever the brand's backend
     *  returned, so a new field shows up without a code change. */
    memberRows() {
      const m = this.member || {}
      const out = {}
      for (const k of Object.keys(m)) {
        const v = m[k]
        if (v === null || v === undefined || typeof v === 'object') continue
        out[k] = k === 'cash' || k === 'balance' ? 'RM ' + Number(v).toFixed(2) : String(v)
      }
      return out
    },
    filters() {
      // Rebuilt on every render rather than held in data(), so a language
      // change repaints the chips instead of leaving the old words behind.
      return [
        { key: 'waiting', label: this.$t('inbox.filterWaiting') },
        { key: 'human', label: this.$t('inbox.filterMine') },
        { key: 'bot', label: this.$t('inbox.filterBot') },
        { key: 'resolved', label: this.$t('inbox.filterDone') },
        { key: '', label: this.$t('inbox.filterAll') },
      ]
    },
    /**
     * True when the brand picker is on "All brands".
     *
     * The server returns every brand's conversations when no X-Tenant-Id is
     * sent, so the list becomes a mixed one — and a row with no brand on it is
     * then unattributable. The label is rendered ONLY in this state; with a
     * brand selected every row is that brand and the chip would be noise.
     */
    allBrands() {
      return tenantHelper.current() === null
    },
    agentDdl() {
      const options = this.agents.map((a) => ({
        // "(support)" marks somebody who is not part of this client's own
        // team -- an agent assigning work needs to know that.
        text: a.is_platform_staff ? `${a.name} (${this.$t('inbox.support')})` : a.name,
        value: a.id,
      }))

      if (this.can.assign_others) return options

      /*
       * Without Assign Conversation you may still CLAIM — take an unassigned
       * one for yourself, or release one that is already yours. So the picker
       * offers exactly you, plus whoever currently holds it so the box is not
       * blank when somebody else does.
       */
      /*
       * Matched on e-mail, not on the stored user id: session storage holds the
       * UUID (SESSION.USER_ID is 'uuid') while this list is keyed by the
       * numeric users.id, and comparing those two silently matches nothing --
       * which would leave an agent with an empty picker and no way to claim.
       */
      const myEmail = (sessionHelper.getUserEmail() || '').toLowerCase()
      const me = this.agents.find((a) => (a.email || '').toLowerCase() === myEmail)
      const current = this.selected && this.selected.assigned_user_id

      return options.filter((o) => (me && o.value === me.id) || o.value === current)
    },
    /**
     * The thread, annotated for rendering.
     *
     * Three things the template cannot work out per-row on its own:
     *
     *   side        us on the right, everyone else on the left. "Us" is the
     *               AGENT side, not "me" — a conversation worked by two people
     *               across a shift reads as one side of the desk, and flipping
     *               a colleague's reply to the left would make the handover
     *               look like the player said it.
     *   showHeader  only on the FIRST message of a run by the same sender.
     *               Repeating "Player 12:03 / Player 12:03" down a burst is
     *               what made the old thread look like a log file.
     *   initials    for the avatar, so a run of bubbles still has an owner
     *               once the name is hidden.
     */
    threadRows() {
      let prev = null

      return this.messages.map((m) => {
        const isAgent = m.sender_type === 'agent'
        const isSystem = m.sender_type === 'system'

        // A private note from an agent is still our side, but it was never
        // sent — it gets its own treatment rather than the accent.
        const kind = isSystem
          ? 'system'
          : m.is_private
          ? 'note'
          : isAgent
          ? 'agent'
          : m.sender_type === 'bot'
          ? 'bot'
          : 'contact'

        const row = {
          ...m,
          kind,
          side: isAgent ? 'right' : 'left',
          // A sender change starts a new run. `is_private` counts as a change
          // so a note never hides under a reply's header.
          showHeader: !prev || prev.sender_type !== m.sender_type || prev.is_private !== m.is_private,
          initials: this.initialsFor(m),
        }

        if (!isSystem) prev = m
        return row
      })
    },
    /**
     * The first message this agent has not seen, or null when caught up.
     *
     * Only ever a message somebody ELSE sent: an agent's own reply is not
     * unread work, and a divider sitting above your own last message reads as
     * a bug.
     */
    firstUnreadId() {
      if (!this.lastReadId) return null
      const m = this.messages.find(
        (x) => Number(x.id) > this.lastReadId && x.sender_type !== 'agent'
      )
      return m ? m.id : null
    },
    /** Shaped like every other listing's, so the shared footer renders it. */
    queuePagination() {
      return {
        pageNumber: this.page,
        pageSize: this.pageSize,
        total: this.total,
        tableRowsCounts: this.conversations.length,
      }
    },
  },
  watch: {
    search() {
      // Debounced: an agent typing a member id would otherwise fire a LIKE
      // query per keystroke.
      clearTimeout(this.searchTimer)
      // Page 1: staying on page 3 of a freshly narrowed queue is an empty
      // pane that reads as "no results".
      this.searchTimer = setTimeout(() => this.loadFirstPage(), 350)
    },
  },
  created() {
    this.loadList()
    this.loadAgents()
    this.listTimer = setInterval(() => {
      if (!document.hidden) this.loadList(true)
    }, 5000)
    // The proposal page opens in another tab. When it saves or sends, it
    // writes a marker to localStorage; the `storage` event fires here, in
    // this tab, and the Proposal pane refreshes without a reload.
    window.addEventListener('storage', this.onStorage)
  },
  beforeDestroy() {
    window.removeEventListener('storage', this.onStorage)
    clearInterval(this.listTimer)
    clearInterval(this.threadTimer)
    clearTimeout(this.searchTimer)
    // No document.title reset here any more: vue-meta restores the next
    // route's title on its own, and clearing it by hand was the other half of
    // the race described on `page()`.
  },
  methods: {
    /**
     * Fetch the member's details, now.
     *
     * Deliberately has no fallback to anything stored: if the call fails the
     * agent is told, rather than shown a number from some earlier moment that
     * may no longer be the member's.
     */
    /** Briefs from this conversation. Cheap (one DB read on ai_bot), so it is
     *  fetched on open rather than on a press — unlike the member lookup, it
     *  is not an outbound call to a brand's backend. */
    crmState(l) { return (l && l.oneSales && l.oneSales.status) || 'none' },
    crmLabel(l) {
      const o = (l && l.oneSales) || {}
      const kind = o.kind ? (this.$t('inbox.crmKind' + o.kind.charAt(0).toUpperCase() + o.kind.slice(1)) || o.kind) : ''
      switch (o.status) {
        case 'pending': return this.$t('inbox.crmPending')
        case 'created': return this.$t('inbox.crmCreated') + (o.ref ? ' · ' + o.ref.slice(0, 8) : '')
        case 'existing': return this.$t('inbox.crmExisting') + (kind ? ' (' + kind + (o.ref ? ' ' + o.ref : '') + ')' : '')
        case 'failed': return this.$t('inbox.crmFailed') + (o.error ? ' · ' + String(o.error).slice(0, 80) : '')
        default: return this.$t('inbox.crmNotSent')
      }
    },
    crmVariant(l) {
      return { pending: 'info', created: 'success', existing: 'secondary', failed: 'danger' }[this.crmState(l)] || 'light'
    },
    async pushLeadCrm(l) {
      if (!this.selected || this.crmBusy) return
      this.crmBusy = l.leadRef
      this.error = ''
      try {
        const res = await inboxService.pushLeadToOneSales(this.selected.uuid, l.leadRef)
        const fresh = res && res.data
        if (fresh) this.leads = this.leads.map((x) => (x.leadRef === fresh.leadRef ? { ...x, ...fresh } : x))
      } catch (e) {
        this.error = this.readError(e)
      } finally {
        this.crmBusy = null
      }
    },
    openCrmAdd() {
      const c = (this.selected && this.selected.contact) || {}
      const label = c.label && !/^#?\d+$/.test(c.label) ? c.label : ''
      this.crmAdd = { open: true, busy: false, error: '', name: label, phone: '', email: '', company: '', notes: '' }
    },
    async submitCrmAdd() {
      if (!this.selected || this.crmAdd.busy) return
      const f = this.crmAdd
      if (!f.phone.trim() && !f.email.trim()) { f.error = this.$t('inbox.crmNeedContact'); return }
      f.busy = true
      f.error = ''
      try {
        const res = await inboxService.addLeadToOneSales(this.selected.uuid, {
          name: f.name.trim(), phone: f.phone.trim() || undefined, email: f.email.trim() || undefined,
          company: f.company.trim() || undefined, notes: f.notes.trim() || undefined,
        })
        const fresh = res && res.data
        if (fresh) this.leads = [fresh, ...this.leads.filter((x) => x.leadRef !== fresh.leadRef)]
        this.crmAdd = { ...f, open: false, busy: false }
      } catch (e) {
        f.error = this.readError(e)
        f.busy = false
      }
    },
    async loadLeads() {
      if (!this.selected) return
      const uuid = this.selected.uuid
      this.leadsLoading = true
      try {
        const res = await inboxService.getLeads(uuid)
        if (this.selected && this.selected.uuid === uuid) this.leads = (res && res.data) || []
      } catch (e) {
        this.leads = []
      } finally {
        this.leadsLoading = false
      }
    },

    /**
     * The brand's own colour for this row's reason, when it picked one in
     * Brand details. Inline, so it beats the built-in class styling (including
     * overdue red) — a brand that chose a colour chose it for every state.
     */
    /** The reason tint is for rows a person still has to deal with. A
     *  resolved deposit question is just a resolved conversation. */
    isOpenHandoff(c) {
      return c.status === 'waiting' || c.status === 'human'
    },
    rowStyle(c) {
      if (!c.reason_color) return null
      return { borderLeft: '4px solid ' + c.reason_color, background: this.tint(c.reason_color, 0.1) }
    },
    chipStyle(c) {
      if (!c.reason_color) return null
      return { background: this.tint(c.reason_color, 0.18), color: c.reason_color }
    },
    tint(hex, alpha) {
      const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '')
      if (!m) return null
      return `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, ${alpha})`
    },

    /** Translated when we have a word for it; the raw reason otherwise, so a
     *  reason a brand adds in its yaml still shows without a console deploy. */
    reasonLabel(r) {
      const key = 'inbox.reason_' + r
      return this.$te(key) ? this.$t(key) : String(r).replace(/_/g, ' ')
    },

    toneLabel(t) {
      const key = 'inbox.tone_' + t
      return this.$te(key) ? this.$t(key) : String(t)
    },
    /** The brand's colour for a tone (reason_colors.tone_<x>), else the built-in class. */
    toneChipStyle(c) {
      if (!c || !c.tone_color) return null
      return { background: this.tint(c.tone_color, 0.18), color: c.tone_color }
    },

    /**
     * The desk corrects the bot. Saved on the message (so it reads as
     * labelled) and sent to the bot as an example; the bot not being
     * reachable keeps the label here and says so.
     */
    async labelTone(m, label) {
      if (!label || !this.selected) return
      try {
        const res = await inboxService.labelTone(this.selected.uuid, m.uuid, label)
        const d = (res && res.data) || {}
        m.tone_label = d.tone_label || label
        sharedHelper.alertSuccessMessage(i18n.t(d.synced ? 'inbox.toneLabelSaved' : 'inbox.toneLabelNotSynced'))
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(i18n.t('message.error'))
      }
    },

    async openPush() {
      this.push = { show: true, tenantId: tenantHelper.current(), memberId: '', body: '', busy: false }
      if (this.allBrands && !this.pushBrands.length) {
        try {
          const res = await tenantService.list()
          this.pushBrands = res.data || []
        } catch (e) {
          this.pushBrands = []
        }
      }
    },
    async sendPush() {
      const memberId = this.push.memberId.trim()
      const body = this.push.body.trim()
      if (!this.push.tenantId || !memberId || !body) return
      this.push.busy = true
      try {
        const res = await inboxService.pushToMember(Number(this.push.tenantId), memberId, body)
        const d = (res && res.data) || {}
        this.push.show = false
        sharedHelper.alertSuccessMessage(i18n.t('inbox.pushSent'))
        await this.loadFirstPage()
        const row = this.conversations.find((c) => c.uuid === d.uuid)
        if (row) this.openConversation(row)
      } catch (e) {
        const r = e && e.response
        const code = r && r.data && r.data.code
        sharedHelper.alertErrorMessageWithoutCode(
          code === 'member_unknown' ? i18n.t('inbox.pushMemberUnknown') : ((r && r.data && r.data.error) || i18n.t('message.error')),
        )
      } finally {
        this.push.busy = false
      }
    },

    async deleteMessage(m) {
      const ok = await sharedHelper.confirmDestructive({
        title: i18n.t('inbox.deleteMessageTitle'),
        text: i18n.t(this.selected && this.selected.channel === 'webchat' && m.sender_type === 'agent' && !m.is_private
          ? 'inbox.deleteMessageTextVisitor'
          : 'inbox.deleteMessageText'),
        confirmText: i18n.t('inbox.deleteMessage'),
      })
      if (!ok) return
      try {
        const res = await inboxService.deleteMessage(this.selected.uuid, m.uuid)
        const d = (res && res.data) || {}
        await this.loadThread(true)
        if (d.reaches_visitor) {
          sharedHelper.alertSuccessMessage(i18n.t(d.retracted ? 'inbox.messageDeletedBoth' : 'inbox.messageDeletedInboxOnly'))
        } else {
          sharedHelper.alertSuccessMessage(i18n.t('inbox.messageDeletedDone'))
        }
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.readError(e))
      }
    },

    onStorage(e) {
      if (e && e.key === 'proposals:changed' && this.selected) this.loadLeads()
    },

    /** Cheap (one DB read on ai_bot); fetched on open like the briefs. */
    async loadMemory() {
      if (!this.selected) return
      const uuid = this.selected.uuid
      this.memoryLoading = true
      try {
        const res = await inboxService.getMemory(uuid)
        if (this.selected && this.selected.uuid === uuid) {
          this.memory = (res && res.data) || null
          this.memoryEnabled = Boolean(res && res.enabled)
        }
      } catch (e) {
        this.memory = null
      } finally {
        this.memoryLoading = false
      }
    },

    /** PDPA forget-me. Destructive and immediate, so it asks first. */
    async forgetMemory() {
      const ok = await sharedHelper.confirmDestructive({
        title: i18n.t('inbox.forgetMemoryTitle'),
        text: i18n.t('inbox.forgetMemoryText'),
        confirmText: i18n.t('inbox.forgetMemory'),
      })
      if (!ok) return
      try {
        await inboxService.forgetMemory(this.selected.uuid)
        this.memory = null
        sharedHelper.alertSuccessMessage(i18n.t('inbox.memoryForgotten'))
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode((e && e.message) || String(e))
      }
    },

    /** "2026-11-02T06:19:00.000Z" → the agent's local "02/11/2026, 14:19". */

    fmtAt(iso) {

      if (!iso) return '—'

      const d = new Date(iso)

      if (Number.isNaN(d.getTime())) return String(iso)

      return d.toLocaleString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })

    },

    async lookUpMember() {
      if (!this.selected) return
      this.memberLoading = true
      try {
        const res = await inboxService.getMember(this.selected.uuid)
        this.member = (res && res.data) || null
        if (!this.member) sharedHelper.alertErrorMessageWithoutCode(this.$t('inbox.memberNotFound'))
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.readError(e))
      } finally {
        this.memberLoading = false
      }
    },

    async loadList(quiet = false) {
      if (!quiet) this.loadingList = true
      try {
        const res = await inboxService.getConversations({
          status: this.filter,
          mine: this.mine,
          q: this.search,
          pageNumber: this.page,
          pageSize: this.pageSize,
        })
        const rows = res.data || []
        this.conversations = rows
        this.total = res.meta ? res.meta.total : rows.length
        if (res.meta && res.meta.can) this.can = res.meta.can
        if (res.meta && res.meta.urgent_after_seconds) {
          this.urgentAfterSeconds = res.meta.urgent_after_seconds
        }

        /*
         * The 5-second poll keeps whatever page the agent is on -- resetting to
         * page 1 under them every five seconds would make any page but the
         * first unusable. The trade-off is that a new waiting conversation
         * appears on page 1 while they are reading page 3; the title count and
         * the alert still fire, because both come from the app-wide poller in
         * state/inbox-alert.js, which is not paged by this screen.
         */
        if (this.page > 1 && rows.length === 0 && this.total > 0) {
          // The last row on this page was resolved or reassigned away. Falling
          // back beats showing "Nothing here" on a queue that has work in it.
          this.page = Math.max(1, Math.ceil(this.total / this.pageSize))
          return this.loadList(quiet)
        }

        this.error = ''
      } catch (e) {
        this.error = this.readError(e)
      } finally {
        this.loadingList = false
      }
    },

    async loadAgents() {
      try {
        const res = await inboxService.getAgents()
        this.agents = res.data || []
      } catch (e) {
        this.agents = []
      }
    },

    /**
     * The bell in this screen's header.
     *
     * Toggles the app-wide alert, not a per-screen one: muting it here means
     * muting it everywhere, which is what an agent asking for quiet means.
     */
    toggleSound() {
      inboxAlert.toggleSound()
    },

    async openConversation(c) {
      this.selected = c
      // Cleared on every open: a number belonging to the previous conversation
      // on this one's header is the worst possible version of this feature.
      this.member = null
      this.leads = []
      this.memory = null
      this.messages = []
      this.hasMore = false
      this.mobileView = 'thread'
      this.loadMemory()
      // Leads come from the first thread load, once we know the brand's skills.
      this.loadingThread = true
      clearInterval(this.threadTimer)
      await this.loadThread()
      this.loadingThread = false
      // Not scrollToBottom: see openAtFirstUnread.
      this.openAtFirstUnread()
      this.threadTimer = setInterval(() => {
        if (!document.hidden && this.selected) this.loadThread(true)
      }, 4000)
    },

    async loadThread(quiet = false) {
      if (!this.selected) return
      try {
        const res = await inboxService.getConversation(this.selected.uuid)
        const d = res.data || {}
        const atBottom = this.isAtBottom()
        const grew = (d.messages || []).length !== this.messages.length

        this.messages = d.messages || []
        this.hasMore = !!d.has_more
        const firstLoad = this.selected.skills === undefined
        this.selected = {
          ...this.selected,
          status: d.status,
          brand: d.brand !== undefined ? d.brand : this.selected.brand,
          assigned_user_id: d.assigned_user_id,
          handover_reason: d.handover_reason !== undefined ? d.handover_reason : this.selected.handover_reason,
          reason_color: d.reason_color !== undefined ? d.reason_color : this.selected.reason_color,
          last_tone: d.last_tone !== undefined ? d.last_tone : this.selected.last_tone,
          tone_color: d.tone_color !== undefined ? d.tone_color : this.selected.tone_color,
          skills: d.skills !== undefined ? d.skills : this.selected.skills,
          // The per-brand switches the context pane's tabs read (One Sales,
          // Proposal). The list rows do not carry them, and this merge used
          // to drop them too, so hasOneSales was false for every brand and
          // the tab never appeared.
          features: d.features !== undefined ? d.features : this.selected.features,
          channel: d.channel || this.selected.channel,
          contact: d.contact || this.selected.contact,
        }

        /*
         * Where this agent stopped reading, captured on the OPENING load only.
         *
         * The server moves the watermark the moment the thread is fetched, so
         * every later poll reports "all read" — keeping the first answer is
         * what holds the divider in place while the agent reads, instead of it
         * vanishing four seconds after it appeared.
         */
        if (!quiet) this.lastReadId = Number(d.last_read_message_id || 0)
        if (firstLoad && this.wantsLeads) this.loadLeads()

        // Only follow the conversation down if the agent was already at the
        // bottom. Yanking them away from a receipt they are reading is worse
        // than a missed scroll.
        if (grew && (atBottom || !quiet)) this.scrollToBottom()
      } catch (e) {
        this.error = this.readError(e)
      }
    },

    async loadOlder() {
      if (!this.messages.length || this.loadingMore) return
      this.loadingMore = true
      const pane = this.$refs.thread
      const prevHeight = pane ? pane.scrollHeight : 0
      try {
        const res = await inboxService.getConversation(this.selected.uuid, this.messages[0].id)
        const older = (res.data || {}).messages || []
        this.messages = older.concat(this.messages)
        this.hasMore = !!(res.data || {}).has_more
        // Keep the reading position: without this the pane jumps to the top and
        // the agent loses the line they were on.
        this.$nextTick(() => {
          if (pane) pane.scrollTop = pane.scrollHeight - prevHeight
        })
      } catch (e) {
        this.error = this.readError(e)
      } finally {
        this.loadingMore = false
      }
    },

    isAtBottom() {
      const el = this.$refs.thread
      if (!el) return true
      return el.scrollHeight - el.scrollTop - el.clientHeight < 60
    },

    /**
     * Open the thread where this agent stopped reading.
     *
     * Landing at the newest line is right for a thread you are already in, and
     * wrong for one you are opening: on a conversation that ran all night the
     * part you need is the part you have not read, and the first thing you
     * scroll back past is exactly that.
     *
     * Falls back to the bottom when there is nothing unread, or when the
     * unread run starts so near the end that a divider would just push the
     * newest line off screen.
     */
    openAtFirstUnread() {
      const first = this.firstUnreadId
      if (!first) return this.scrollToBottom()

      this.$nextTick(() => {
        const pane = this.$refs.thread
        const el = pane && pane.querySelector(`[data-mid="${first}"]`)
        if (!pane || !el) return this.scrollToBottom()

        // A little above the divider, so the last thing they DID read is still
        // visible — context for what follows.
        pane.scrollTop = Math.max(0, el.offsetTop - pane.offsetTop - 48)
      })
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.thread
        if (el) el.scrollTop = el.scrollHeight
      })
    },

    async send() {
      const body = this.draft.trim()
      if (!body || this.sending) return
      this.sending = true
      try {
        await inboxService.reply(this.selected.uuid, body, this.isPrivate)
        this.draft = ''
        this.isPrivate = false
        await this.loadThread()
        this.scrollToBottom()
        await this.loadList(true)
      } catch (e) {
        this.error = this.readError(e)
      } finally {
        this.sending = false
      }
    },

    pickFile() {
      this.$refs.file.click()
    },

    async onFile(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file || this.uploading) return
      this.uploading = true
      try {
        await inboxService.sendAttachment(this.selected.uuid, file, this.draft.trim())
        this.draft = ''
        await this.loadThread()
        this.scrollToBottom()
        await this.loadList(true)
      } catch (e) {
        this.error = this.readError(e)
      } finally {
        this.uploading = false
      }
    },

    /** A filter or search changed, so the page number is meaningless now. */
    loadFirstPage() {
      this.page = 1
      return this.loadList()
    },

    goToPage(pageNumber) {
      this.page = pageNumber
      return this.loadList()
    },

    async assignTo(userId) {
      await inboxService.assign(this.selected.uuid, userId)
      await this.loadThread()
      await this.loadList(true)
    },

    /**
     * Take it over now — stop the bot answering, without typing yet.
     *
     * ── Why a button when replying already does this ────────────────────
     * Replying takes over implicitly, and that is right when you ARE replying.
     * The case this exists for is the other one: a player firing off questions
     * faster than anyone can answer, where every extra second is another bot
     * reply the agent will have to read, correct, or apologise for. Pressing
     * this stops the bot on the spot and claims the conversation, and the agent
     * types when they get to it.
     *
     * Claiming happens server-side (ConversationController::status) rather than
     * as a second call from here, so the thread gets one system line instead of
     * two and the two writes cannot half-apply.
     *
     * Reversible with "Back to bot" or by resolving, so no confirmation.
     */
    async takeOver() {
      if (!this.selected) return
      await inboxService.setStatus(this.selected.uuid, 'human')
      await this.loadThread()
      await this.loadList(true)
    },

    /**
     * Hand the conversation back to the bot.
     *
     * The escape hatch for an agent who cannot keep up: the player keeps
     * getting answers instead of sitting in a silent chat waiting for someone
     * who is three conversations behind.
     *
     * ── Why this is not Resolve ─────────────────────────────────────────
     * Resolve means "this is finished" — it stamps `resolved_at`, which is what
     * the handling-time figures are measured from. Pressing it just to escape a
     * queue would quietly report every overflow as a closed ticket. This leaves
     * the conversation open and simply stops claiming a human is on it.
     *
     * Both push `resolved` to ai_bot, because ai_bot has only one question to
     * answer — may the bot speak — and for that the two cases are identical.
     *
     * Reversible with no ceremony: replying takes it back (reply() sets the
     * status to human again), which is why there is no confirmation here.
     */
    async handBackToBot() {
      if (!this.selected) return
      await inboxService.setStatus(this.selected.uuid, 'bot')
      await this.loadThread()
      await this.loadList(true)
    },

    async resolve() {
      if (!this.selected) return
      await inboxService.setStatus(this.selected.uuid, 'resolved')
      await this.loadThread()
      await this.loadList(true)
    },

    /** Enter sends, Shift+Enter is a newline. Agents type fast. */
    onKeydown(e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        this.send()
      }
    },

    /**
     * Two letters for the avatar.
     *
     * Falls back to a dot rather than an empty circle — a blank disc reads as
     * a broken image, and a name is not always there (an unidentified visitor
     * has no display name at all).
     */
    initialsFor(m) {
      if (m.sender_type === 'bot' || m.sender_type === 'system') return ''

      const name =
        m.sender_type === 'agent'
          ? m.sender_name || this.$t('inbox.senderAgent')
          : (this.selected && this.selected.contact && this.selected.contact.label) || ''

      const parts = String(name).trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return '·'

      // CJK names have no spaces, so the "first letter of each word" rule gives
      // one character — which is the right answer for them.
      const first = parts[0][0] || ''
      const second = parts.length > 1 ? parts[parts.length - 1][0] : ''
      return (first + second).toUpperCase()
    },

    waitLabel(seconds) {
      if (seconds == null) return ''
      if (seconds < 60) return seconds + 's'
      if (seconds < 3600) return Math.floor(seconds / 60) + 'm'
      return Math.floor(seconds / 3600) + 'h'
    },

    /**
     * The row the shift lead is scanning for.
     *
     * The threshold is a setting, not a constant — see `urgentAfterSeconds`.
     * It used to be a hardcoded 600, which quietly made one desk's service
     * promise everybody's.
     */
    isUrgent(c) {
      return (
        c.status === 'waiting' &&
        c.waiting_seconds != null &&
        c.waiting_seconds > this.urgentAfterSeconds
      )
    },

    senderLabel(m) {
      if (m.sender_type === 'contact') return this.$t('inbox.senderPlayer')
      if (m.sender_type === 'bot') return this.$t('inbox.senderBot')
      if (m.sender_type === 'system') return this.$t('inbox.senderSystem')
      // The agent's own name, never a translated role: a thread worked by two
      // people across a shift has to stay readable afterwards.
      return m.sender_name || this.$t('inbox.senderAgent')
    },

    isImage(a) {
      return a.mime && a.mime.indexOf('image/') === 0
    },

    time(ts) {
      if (!ts) return ''
      return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    readError(e) {
      return (e && e.response && e.response.data && e.response.data.error) || this.$t('inbox.somethingWrong')
    },
  },
}
</script>

<template>
  <Layout>
    <div class="inbox">
      <!-- ── Queue ─────────────────────────────────────────────────────── -->
      <aside class="pane-list" :class="{ 'd-none d-md-flex': mobileView === 'thread' }">
        <div class="pane-head">
          <input v-model="search" class="form-control form-control-sm" :placeholder="$t('inbox.searchPlaceholder')" />
          <button class="btn btn-sm btn-link px-1" :title="alert.soundOn ? $t('inbox.alertsOn') : $t('inbox.alertsOff')" @click="toggleSound">
            <i class="mdi" :class="alert.soundOn ? 'mdi-bell' : 'mdi-bell-off text-muted'"></i>
          </button>
          <!-- Start a thread with a member who has not written in. Website
               brands only, and only members the site signed in: the server
               says no otherwise, and the dialog explains. -->
          <button v-if="can.push" class="btn btn-sm btn-link px-1" :title="$t('inbox.pushButton')" @click="openPush">
            <i class="mdi mdi-message-plus-outline"></i>
          </button>
        </div>

        <div class="pane-head pt-0">
          <div class="btn-group btn-group-sm flex-grow-1">
            <button
              v-for="f in filters"
              :key="f.key"
              type="button"
              class="btn"
              :class="filter === f.key ? 'btn-primary' : 'btn-outline-secondary'"
              @click="filter = f.key; loadFirstPage()"
            >{{ f.label }}</button>
          </div>
        </div>

        <div class="px-2 pb-1">
          <div class="custom-control custom-checkbox">
            <input id="mine" v-model="mine" type="checkbox" class="custom-control-input" @change="loadFirstPage()" />
            <label class="custom-control-label small" for="mine">{{ $t('inbox.assignedToMe') }}</label>
          </div>
        </div>

        <!-- Said out loud. An agent on a Read Own Conversation role sees an
             empty queue whenever nothing is assigned to them, and "Nothing
             here" alone reads as a broken inbox rather than as a rule. -->
        <div v-if="can.own_only" class="px-2 pb-1">
          <div class="alert alert-secondary py-1 px-2 mb-0 small">
            {{ $t('inbox.ownOnlyNotice') }}
          </div>
        </div>

        <div class="conv-list">
          <div v-if="loadingList" class="text-muted small p-2">{{ $t('inbox.loading') }}</div>
          <div v-else-if="!conversations.length" class="text-muted small p-2">{{ $t('inbox.empty') }}</div>

          <button
            v-for="c in conversations"
            :key="c.uuid"
            type="button"
            class="conv"
            :class="[{ active: selected && selected.uuid === c.uuid, urgent: isUrgent(c), tinted: !!c.reason_color && isOpenHandoff(c) }, c.handover_reason && isOpenHandoff(c) ? 'reason-' + c.handover_reason : '']"
            :style="isOpenHandoff(c) ? rowStyle(c) : null"
            @click="openConversation(c)"
          >
            <div class="d-flex align-items-center">
              <span class="nm font-weight-bold text-truncate" :style="c.reason_color && isOpenHandoff(c) ? { color: c.reason_color } : null">{{ c.contact.label }}</span>
              <!-- Unverified = the id was typed by the host page, not signed. An
                   agent must not read account details off it alone.
                   Sits right after the name, NOT pushed to the right edge —
                   space-between put it exactly under the absolutely-positioned
                   unread count, where it was invisible. -->
              <span
                v-if="c.contact.external_id && !c.contact.is_verified"
                class="badge badge-warning ml-1 flex-shrink-0"
                :title="$t('inbox.unverifiedTitle')"
              >?</span>
            </div>
            <!-- Only on "All brands". With one brand selected every row is
                 that brand and this would be a chip on every line saying the
                 same thing. -->
            <div v-if="allBrands && c.brand" class="brandline">
              <i class="mdi mdi-domain"></i> {{ c.brand }}
            </div>
            <div class="d-flex justify-content-between align-items-center mt-1">
              <span class="badge" :class="'st-' + c.status">{{ c.status }}</span>
              <span
                v-if="c.waiting_seconds != null"
                class="small"
                :class="isUrgent(c) ? 'text-danger font-weight-bold' : 'text-muted'"
              >{{ $t('inbox.waitingFor', [waitLabel(c.waiting_seconds)]) }}</span>
            </div>
            <!-- Why a human was called in, as a coloured chip: a deposit row
                 reads as money at a glance, not as one more overdue red. -->
            <div v-if="c.handover_reason || (c.last_tone && c.last_tone !== 'neutral')" class="mt-1">
              <span v-if="c.handover_reason" class="reason-chip" :class="'rc-' + c.handover_reason" :style="chipStyle(c)">{{ reasonLabel(c.handover_reason) }}</span>
              <!-- What the bot read in the customer's last line. Neutral is
                   not worth a chip; the other four are what a desk triages on. -->
              <span v-if="c.last_tone && c.last_tone !== 'neutral'" class="reason-chip tone-chip" :class="'tc-' + c.last_tone" :style="toneChipStyle(c)">{{ toneLabel(c.last_tone) }}</span>
            </div>
            <div v-if="c.assignee" class="small text-muted">→ {{ c.assignee }}</div>
            <span v-if="c.unread" class="badge badge-danger unread">{{ c.unread }}</span>
          </button>
        </div>

        <!-- Outside .conv-list so it does not scroll away with the rows. The
             same footer every listing in the app uses. -->
        <div v-if="total > pageSize" class="px-2 pb-2 border-top">
          <ListingPagination :pagination="queuePagination" @change="goToPage" />
        </div>
      </aside>

      <!-- ── Thread ───────────────────────────────────────────────────── -->
      <section class="pane-thread" :class="{ 'd-none d-md-flex': mobileView === 'list' }">
        <div v-if="!selected" class="p-4 text-muted">{{ $t('inbox.pickOne') }}</div>

        <template v-else>
          <div class="thread-head">
            <button class="btn btn-sm btn-link d-md-none px-1" @click="mobileView = 'list'">
              <i class="mdi mdi-arrow-left"></i>
            </button>
            <!-- Name first, on its own; chips wrap underneath it on a narrow
                 pane instead of squeezing the name down to one letter. The
                 actions stay on the right as one unbreakable group. -->
            <div class="head-id flex-grow-1 min-w-0">
              <div class="d-flex align-items-center flex-wrap head-chips">
                <strong class="head-name">{{ selected.contact.label }}</strong>
                <span class="badge" :class="'st-' + selected.status">{{ selected.status }}</span>
                <span v-if="selected.handover_reason && isOpenHandoff(selected)" class="reason-chip" :class="'rc-' + selected.handover_reason" :style="chipStyle(selected)">{{ reasonLabel(selected.handover_reason) }}</span>
                <span v-if="selected.last_tone && selected.last_tone !== 'neutral'" class="reason-chip tone-chip" :class="'tc-' + selected.last_tone" :style="toneChipStyle(selected)">{{ toneLabel(selected.last_tone) }}</span>
                <!-- The one place it matters most: an agent about to answer
                     needs to know whose player this is BEFORE they type. -->
                <span v-if="allBrands && selected.brand" class="brandtag">
                  <i class="mdi mdi-domain"></i> {{ selected.brand }}
                </span>
              </div>
              <div v-if="selected.contact.external_id" class="small text-muted">
                ID {{ selected.contact.external_id }}
                <span v-if="!selected.contact.is_verified" class="text-warning">{{ $t('inbox.unverified') }}</span>

              </div>
            </div>

            <!-- A searchable picker rather than a menu of items: with thirty
                 agents the old list was a scroll with no way to type a name,
                 and the one entry that was not an agent ("Unassign") sat above
                 a divider where it was easy to hit by accident. Clearing the
                 box unassigns, which is the same gesture as every other
                 clearable field in the app. -->
            <div class="head-actions">
            <MultiSelect
              :value="selected.assigned_user_id"
              :options="agentDdl"
              :multiple="false"
              :placeholder="$t('inbox.unassigned')"
              class="mr-1 head-assign"
              @input="assignTo"
            />

            <!-- The bot is still answering this one. Stop it and claim the
                 conversation without having to type first. -->
            <button
              v-if="selected.status === 'bot' || selected.status === 'waiting'"
              class="btn btn-sm btn-primary mr-1"
              :title="$t('inbox.takeOverHint')"
              @click="takeOver"
            >
              <!-- mdi-headset: verified present in this font build. A glyph
                   this version does not ship renders as blank space, not as an
                   error — the bot avatar hit exactly that. -->
              <i class="mdi mdi-headset"></i> {{ $t('inbox.takeOver') }}
            </button>
            <!-- Only while a human is (or is meant to be) on it. On a thread
                 the bot already owns there is nothing to hand back. -->
            <button
              v-if="selected.status === 'human' || selected.status === 'waiting'"
              class="btn btn-sm btn-outline-secondary mr-1"
              :title="$t('inbox.backToBotHint')"
              @click="handBackToBot"
            >
              <i class="mdi mdi-robot"></i> {{ $t('inbox.backToBot') }}
            </button>
            <button v-if="selected.status !== 'resolved'" class="btn btn-sm btn-outline-success" @click="resolve">
              {{ $t('inbox.resolve') }}
            </button>
            </div>
          </div>

          <div ref="thread" class="thread">
            <div v-if="loadingThread" class="text-muted small">{{ $t('inbox.loading') }}</div>
            <div v-if="hasMore" class="text-center mb-2">
              <button class="btn btn-sm btn-link" :disabled="loadingMore" @click="loadOlder">
                {{ loadingMore ? $t('inbox.loading') : $t('inbox.loadEarlier') }}
              </button>
            </div>

            <template v-for="m in threadRows">
              <!-- System lines are the audit trail: who assigned, who resolved.
                   Centred and small, so they read as margin notes rather than
                   competing with what a person actually said. -->
              <div v-if="m.kind === 'system'" :key="m.id" class="sysline">
                <span>{{ m.body }}</span>
              </div>

              <!-- The line Slack and every mail client draw: everything below
                   it arrived since this agent last looked. -->
              <div v-if="m.id === firstUnreadId" :key="'u' + m.id" class="unread-line">
                <span>{{ $t('inbox.newSinceLastVisit') }}</span>
              </div>

              <div
                v-if="m.kind !== 'system'"
                :key="m.id"
                :data-mid="m.id"
                class="row-msg"
                :class="[m.side, m.kind, { grouped: !m.showHeader }]"
              >
                <!-- One avatar per run, not per bubble. The spacer keeps the
                     bubbles in a column when the avatar is hidden. -->
                <div class="ava" :class="m.kind" :aria-hidden="!m.showHeader">
                  <template v-if="m.showHeader">
                    <!-- mdi-robot, not mdi-robot-outline: this build of the
                         icon font has no outline variant, and a missing glyph
                         renders as an empty circle rather than as an error. -->
                    <i v-if="m.kind === 'bot'" class="mdi mdi-robot"></i>
                    <span v-else>{{ m.initials }}</span>
                  </template>
                </div>

                <div class="stack">
                  <div v-if="m.showHeader" class="who">
                    <span class="nm">{{ senderLabel(m) }}</span>
                    <span v-if="m.is_private" class="tag-note">{{ $t('inbox.internal') }}</span>
                    <span class="ts">{{ time(m.created_at) }}</span>
                  </div>

                  <div class="bub" :class="{ deleted: m.deleted_at }">
                    <div v-if="m.deleted_at" class="txt"><i class="mdi mdi-cancel mr-1"></i>{{ $t('inbox.messageDeleted') }}</div>
                    <div v-else-if="m.body" class="txt">{{ m.body }}</div>
                    <div v-for="(a, i) in (m.attachments || [])" :key="i" class="att">
                      <a v-if="isImage(a)" :href="a.url" target="_blank" rel="noopener">
                        <img :src="a.url" :alt="a.name" />
                      </a>
                      <a v-else class="file" :href="a.url" target="_blank" rel="noopener">
                        <i class="mdi mdi-paperclip"></i> {{ a.name }}
                      </a>
                    </div>
                  </div>

                  <!-- WhatsApp rejects free text outside the 24h window. A tick
                       on a rejected message is worse than showing the failure. -->
                  <div v-if="m.delivery_status === 'failed' && !m.deleted_at" class="undelivered" :title="m.failure_reason">
                    <i class="mdi mdi-alert-circle-outline"></i> {{ $t('inbox.notDelivered') }}
                  </div>
                  <!-- Shown only when the server says this person may; appears
                       on hover so a thread is not a column of bins. -->
                  <button v-if="m.can_delete" type="button" class="msg-del" @click="deleteMessage(m)">
                    <i class="mdi mdi-delete-outline"></i> {{ $t('inbox.deleteMessage') }}
                  </button>
                  <!-- The bot's reading of this line, and the desk's say. The
                       label is an example the bot learns from, so it is one
                       click, not a form. -->
                  <div v-if="m.can_label" class="tone-row">
                    <span class="reason-chip tone-chip" :class="'tc-' + (m.tone_label || m.tone)">
                      {{ toneLabel(m.tone_label || m.tone) }}<template v-if="m.tone_label"> ✓</template>
                    </span>
                    <select class="tone-pick" :value="m.tone_label || ''" @change="labelTone(m, $event.target.value)">
                      <option value="" disabled>{{ $t('inbox.toneLabelPrompt') }}</option>
                      <option v-for="t in toneOptions" :key="t" :value="t">{{ toneLabel(t) }}</option>
                    </select>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <div class="composer">
            <textarea
              v-model="draft"
              class="form-control mb-2"
              rows="2"
              :placeholder="isPrivate ? $t('inbox.notePlaceholder') : $t('inbox.replyPlaceholder')"
              @keydown="onKeydown"
            ></textarea>
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <div class="custom-control custom-checkbox mr-3">
                  <input id="priv" v-model="isPrivate" type="checkbox" class="custom-control-input" />
                  <label class="custom-control-label small" for="priv">{{ $t('inbox.internalNote') }}</label>
                </div>
                <button class="btn btn-sm btn-link px-1" :disabled="uploading" title="Attach a file" @click="pickFile">
                  <i class="mdi mdi-paperclip"></i>
                  <span v-if="uploading" class="small">uploading…</span>
                </button>
                <input ref="file" type="file" class="d-none" accept="image/*,.pdf" @change="onFile" />
              </div>
              <button class="btn btn-sm btn-primary" :disabled="sending || !draft.trim()" @click="send">
                {{ sending ? $t('inbox.sending') : $t('inbox.send') }}
              </button>
            </div>
          </div>
        </template>
      </section>

      <!-- ── Context: who this is, and what they asked for ─────────────── -->
      <!-- Tabs follow the brand's skills: Member needs a jk backend, Proposal
           needs the lead skill. A brand with neither still gets a Customer tab
           when memory is on; with nothing to show, no pane at all. -->
      <aside v-if="selected && (hasJk || hasLead || hasOneSales || memoryEnabled)" class="pane-context d-none d-lg-flex">
        <b-tabs v-model="ctxTab" small card class="ctx-tabs">
          <b-tab v-if="hasJk || memoryEnabled" :title="$t(hasJk ? 'inbox.tabMember' : 'inbox.tabCustomer')">
            <div v-if="hasJk && !selected.contact.external_id" class="small text-muted">{{ $t('inbox.memberAnonymous') }}</div>
            <template v-else-if="hasJk">
              <div class="small text-muted mb-2">ID <code>{{ selected.contact.external_id }}</code>
                <span v-if="!selected.contact.is_verified" class="text-warning ml-1">{{ $t('inbox.unverified') }}</span></div>
              <!-- Pressed, not attached: nothing is fetched until an agent
                   asks, and then it is fresh. See lookUpMember. -->
              <b-button v-if="!member" size="sm" variant="outline-primary" :disabled="memberLoading" @click="lookUpMember">
                <i class="mdi mdi-account-search-outline mr-1"></i>{{ memberLoading ? $t('label.loading') : $t('inbox.lookUpMember') }}
              </b-button>
              <!-- The player card (ai_bot core/jk/user_card.ts): identity, wallet,
                   bank, activity, referral — phone and account number arrive
                   already masked. A backend that returns no card falls back to
                   the flat rows below. -->
              <div v-else-if="member.card" class="player-card">
                <div class="pc-head">
                  <div class="pc-name">{{ member.card.header.name }}<span v-if="member.card.header.username && member.card.header.username !== member.card.header.name" class="pc-user"> ({{ member.card.header.username }})</span></div>
                  <div class="pc-badges">
                    <span v-if="member.card.header.status" class="pc-badge" :class="member.card.header.status === 'ACTIVE' ? 'ok' : 'warn'">{{ member.card.header.status }}</span>
                    <span v-if="member.card.header.type" class="pc-badge">{{ member.card.header.type }}</span>
                    <span v-if="member.card.header.tag" class="pc-badge tag">{{ member.card.header.tag }}</span>
                  </div>
                </div>
                <div class="pc-tiles">
                  <div class="pc-tile">
                    <div class="pc-tile-l">{{ $t('inbox.pcBalance') }}</div>
                    <div class="pc-tile-v">{{ member.card.tiles.balance || '—' }}</div>
                  </div>
                  <div class="pc-tile alt">
                    <div class="pc-tile-l">{{ $t('inbox.pcLifetimeDeposit') }}</div>
                    <div class="pc-tile-v">{{ member.card.tiles.lifetimeDeposit || '—' }}</div>
                    <div v-if="member.card.tiles.lifetimeDepositCount !== undefined" class="pc-tile-s">{{ $t('inbox.pcDeposits', [member.card.tiles.lifetimeDepositCount]) }}</div>
                  </div>
                </div>

                <div class="pc-sec">
                  <div class="pc-sec-t">{{ $t('inbox.pcIdentity') }}</div>
                  <div class="pc-row"><span>{{ $t('inbox.pcUsername') }}</span><b>{{ member.card.identity.username || '—' }}</b></div>
                  <div class="pc-row"><span>{{ $t('inbox.pcInternalId') }}</span><b>{{ member.card.identity.id }}</b></div>
                  <div v-if="member.card.identity.mobile" class="pc-row"><span>{{ $t('inbox.pcMobile') }}</span><b>{{ member.card.identity.mobile }}</b></div>
                  <div v-if="member.card.identity.email" class="pc-row"><span>{{ $t('inbox.pcEmail') }}</span><b>{{ member.card.identity.email }}</b></div>
                </div>

                <div v-if="member.card.wallet.minWithdraw || member.card.wallet.totalBet" class="pc-sec">
                  <div class="pc-sec-t">{{ $t('inbox.pcWallet') }}</div>
                  <div v-if="member.card.wallet.minWithdraw" class="pc-row"><span>{{ $t('inbox.pcMinWithdraw') }}</span><b>{{ member.card.wallet.minWithdraw }}</b></div>
                  <div v-if="member.card.wallet.totalBet" class="pc-row"><span>{{ $t('inbox.pcTurnover') }}</span><b>{{ member.card.wallet.totalBet }}</b></div>
                </div>

                <div v-for="(b, i) in member.card.banks" :key="'bank' + i" class="pc-sec bank">
                  <div class="pc-sec-t">{{ $t('inbox.pcBank') }}<span v-if="member.card.banks.length > 1"> {{ i + 1 }}</span></div>
                  <div class="pc-row"><span>{{ $t('inbox.pcBankName') }}</span><b>{{ b.bank }}</b></div>
                  <div v-if="b.accountName" class="pc-row"><span>{{ $t('inbox.pcAccountName') }}</span><b>{{ b.accountName }}</b></div>
                  <div v-if="b.accountMasked" class="pc-row"><span>{{ $t('inbox.pcAccountNumber') }}</span><b class="mono">{{ b.accountMasked }}</b></div>
                  <div class="pc-row"><span>{{ $t('inbox.pcBankLocked') }}</span><b>{{ b.locked ? $t('inbox.pcYes') + ' 🔒' : $t('inbox.pcNo') }}</b></div>
                </div>

                <div class="pc-sec">
                  <div class="pc-sec-t">{{ $t('inbox.pcActivity') }}</div>
                  <div class="pc-row"><span>{{ $t('inbox.pcJoined') }}</span><b>{{ fmtAt(member.card.activity.joinedAt) }}</b></div>
                  <div class="pc-row"><span>{{ $t('inbox.pcLastSeen') }}</span><b>{{ fmtAt(member.card.activity.lastSeenAt) }}</b></div>
                </div>

                <div v-if="member.card.referral.referredBy || member.card.referral.referLink" class="pc-sec">
                  <div class="pc-sec-t">{{ $t('inbox.pcReferral') }}</div>
                  <div v-if="member.card.referral.referredBy" class="pc-row"><span>{{ $t('inbox.pcReferredBy') }}</span><b>{{ member.card.referral.referredBy }}</b></div>
                  <div v-if="member.card.referral.topReferrer" class="pc-row"><span>{{ $t('inbox.pcTopReferrer') }}</span><b>{{ member.card.referral.topReferrer }}</b></div>
                  <div v-if="member.card.referral.referLink" class="pc-row"><span>{{ $t('inbox.pcReferLink') }}</span><b class="mono pc-link">{{ member.card.referral.referLink }}</b></div>
                </div>

                <div class="pc-foot">{{ $t('inbox.pcLive', [fmtAt(member.card.fetchedAt)]) }}</div>
              </div>
              <table v-else class="table table-sm mb-2 small">
                <tr v-for="(v, k) in memberRows" :key="k"><td class="text-muted" style="width:40%">{{ k }}</td><td>{{ v }}</td></tr>
              </table>
              <b-button v-if="member" size="sm" variant="link" class="p-0" :disabled="memberLoading" @click="lookUpMember">{{ $t('inbox.refreshMember') }}</b-button>
            </template>

            <!-- What the bot knows going in — an agent taking over should not
                 ask for a name the customer gave the bot last week. -->
            <div v-if="memoryEnabled" class="memory-box mt-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <strong class="small"><i class="mdi mdi-brain mr-1"></i>{{ $t('inbox.botRemembers') }}</strong>
                <b-button v-if="memory" size="sm" variant="link" class="p-0 small text-danger" @click="forgetMemory">{{ $t('inbox.forgetMemory') }}</b-button>
              </div>
              <div v-if="memoryLoading" class="small text-muted">{{ $t('label.loading') }}</div>
              <div v-else-if="!memory && !selected.contact.external_id" class="small text-muted">{{ $t('inbox.memoryAnonymous') }}</div>
              <div v-else-if="!memory" class="small text-muted">{{ $t('inbox.noMemory') }}</div>
              <template v-else>
                <table class="table table-sm mb-1 small">
                  <tr v-for="(f, k) in memory.facts" :key="k">
                    <td class="text-muted" style="width:40%">{{ String(k).replace(/_/g, ' ') }}</td>
                    <td>{{ f.value }} <span class="text-muted" :title="f.at">· {{ String(f.at || '').slice(0, 10) }}</span></td>
                  </tr>
                </table>
                <p v-if="memory.summary" class="small mb-0"><span class="text-muted">{{ $t('inbox.memorySummary') }}:</span> {{ memory.summary }}</p>
              </template>
            </div>
          </b-tab>
          <b-tab v-if="hasLead" :title="$t('inbox.tabProposal') + (leads.length ? ` (${leads.length})` : '')">
            <div v-if="leadsLoading" class="small text-muted">{{ $t('label.loading') }}</div>
            <div v-else-if="!leads.length" class="small text-muted">{{ $t('inbox.noLeads') }}</div>
            <div v-for="l in leads" v-else :key="l.leadRef" class="lead-card">
              <div class="d-flex justify-content-between align-items-start">
                <div class="min-w-0">
                  <div class="font-weight-bold text-truncate">{{ (l.fields && l.fields.company) || l.formId }}</div>
                  <div class="small text-muted"><code>{{ l.leadRef }}</code> · {{ String(l.createdAt || '').replace('T', ' ').slice(0, 16) }}</div>
                </div>
                <b-badge :variant="l.proposal ? ({ draft: 'warning', approved: 'info', sent: 'success' }[l.proposal.status] || 'light') : 'light'">
                  {{ l.proposal ? ('v' + l.proposal.version + ' ' + l.proposal.status) : $t('inbox.noProposalYet') }}
                </b-badge>
              </div>
              <div v-if="l.fields" class="small text-muted mt-1 text-truncate">
                {{ [l.fields.pax ? l.fields.pax + ' pax' : '', l.fields.programme_interest, l.fields.location].filter(Boolean).join(' · ') }}
              </div>
              <router-link :to="`/proposals/${l.leadRef}`" target="_blank">
                <b-button size="sm" variant="primary" class="mt-2">{{ l.proposal ? $t('inbox.openProposal') : $t('inbox.draftProposal') }}</b-button>
              </router-link>
            </div>
          </b-tab>
          <!-- One Sales: where each brief went in the brand's CRM, the push
               button for one that did not get there, and filing by hand when
               the bot captured nothing usable. -->
          <b-tab v-if="hasOneSales" :title="$t('inbox.tabOneSales') + (leads.length ? ` (${leads.length})` : '')">
            <div v-if="leadsLoading" class="small text-muted">{{ $t('label.loading') }}</div>
            <div v-else-if="!leads.length" class="small text-muted">{{ $t('inbox.noLeads') }}</div>
            <div v-for="l in leads" v-else :key="'crm-' + l.leadRef" class="lead-card">
              <div class="d-flex justify-content-between align-items-start">
                <div class="min-w-0">
                  <div class="font-weight-bold text-truncate">{{ (l.fields && (l.fields.company || l.fields.name)) || l.formId }}</div>
                  <div class="small text-muted"><code>{{ l.leadRef }}</code> · {{ String(l.createdAt || '').replace('T', ' ').slice(0, 16) }}</div>
                </div>
                <b-badge :variant="crmVariant(l)">{{ crmLabel(l) }}</b-badge>
              </div>
              <div v-if="l.fields" class="small text-muted mt-1">
                {{ [l.fields.name, l.fields.phone, l.fields.email, l.fields.requirement, l.fields.location].filter(Boolean).join(' · ') }}
              </div>
              <b-button
                v-if="crmState(l) === 'none' || crmState(l) === 'failed'"
                size="sm" variant="outline-primary" class="mt-2"
                :disabled="crmBusy === l.leadRef"
                @click="pushLeadCrm(l)"
              >{{ crmBusy === l.leadRef ? $t('label.loading') : $t('inbox.crmRetry') }}</b-button>
            </div>
            <div v-if="!leadsLoading" class="mt-3">
              <b-button v-if="!crmAdd.open" size="sm" variant="outline-secondary" @click="openCrmAdd">{{ $t('inbox.crmAdd') }}</b-button>
              <div v-else class="lead-card">
                <div class="font-weight-bold">{{ $t('inbox.crmAddTitle') }}</div>
                <div class="small text-muted mb-2">{{ $t('inbox.crmAddHint') }}</div>
                <b-form-input v-model="crmAdd.name" size="sm" class="mb-1" :placeholder="$t('inbox.crmName')" />
                <b-form-input v-model="crmAdd.phone" size="sm" class="mb-1" :placeholder="$t('inbox.crmPhone')" />
                <b-form-input v-model="crmAdd.email" size="sm" class="mb-1" :placeholder="$t('inbox.crmEmail')" />
                <b-form-input v-model="crmAdd.company" size="sm" class="mb-1" :placeholder="$t('inbox.crmCompany')" />
                <b-form-textarea v-model="crmAdd.notes" size="sm" rows="2" class="mb-1" :placeholder="$t('inbox.crmNotes')" />
                <div v-if="crmAdd.error" class="small text-danger mb-1">{{ crmAdd.error }}</div>
                <b-button size="sm" variant="primary" :disabled="crmAdd.busy || !crmAdd.name.trim()" @click="submitCrmAdd">{{ crmAdd.busy ? $t('label.loading') : $t('inbox.crmSubmit') }}</b-button>
                <b-button size="sm" variant="link" :disabled="crmAdd.busy" @click="crmAdd.open = false">{{ $t('inbox.crmCancel') }}</b-button>
              </div>
            </div>
          </b-tab>
        </b-tabs>
      </aside>

      <div v-if="error" class="alert alert-danger py-2 small inbox-error">{{ error }}</div>
    </div>

    <!-- ── Message a member ────────────────────────────────────────────── -->
    <b-modal v-model="push.show" :title="$t('inbox.pushTitle')" hide-footer centered>
      <p class="small text-muted">{{ $t('inbox.pushHint') }}</p>
      <b-form-group v-if="allBrands" :label="$t('inbox.pushBrand')">
        <b-form-select v-model="push.tenantId">
          <b-form-select-option :value="null" disabled>{{ $t('inbox.pushBrand') }}</b-form-select-option>
          <b-form-select-option v-for="t in pushBrands" :key="t.tenant_id" :value="t.tenant_id">{{ t.name }}</b-form-select-option>
        </b-form-select>
      </b-form-group>
      <b-form-group :label="$t('inbox.pushMemberId')">
        <b-form-input v-model="push.memberId" placeholder="452029333" />
      </b-form-group>
      <b-form-group :label="$t('inbox.pushBody')">
        <b-form-textarea v-model="push.body" rows="3" />
      </b-form-group>
      <div class="text-right">
        <b-button variant="link" size="sm" @click="push.show = false">{{ $t('label.cancel') }}</b-button>
        <b-button variant="primary" size="sm" :disabled="push.busy || !push.tenantId || !push.memberId.trim() || !push.body.trim()" @click="sendPush">
          {{ push.busy ? $t('label.loading') : $t('inbox.pushSend') }}
        </b-button>
      </div>
    </b-modal>
  </Layout>
</template>

<style scoped>
.inbox { display: flex; gap: 12px; height: calc(100vh - 140px); }
.pane-list {
  display: flex; flex-direction: column; width: 320px; flex: 0 0 320px;
  background: #fff; border-radius: 6px; box-shadow: 0 1px 2px rgba(0,0,0,.06);
}
.memory-box {
  border-top: 1px solid #eff2f7;
  padding-top: 8px;
}

.pane-context {
  display: flex; flex-direction: column; width: 320px; flex: 0 0 320px; min-width: 0;
  border: 1px solid #e6e8ef; border-radius: 6px; background: #fff; overflow: hidden;
}
.pane-context .ctx-tabs { height: 100%; }
.pane-context .ctx-tabs .tab-content { overflow-y: auto; }
.lead-card { border: 1px solid #e6e8ef; border-radius: 6px; padding: 10px; margin-bottom: 8px; }
.pane-thread {
  display: flex; flex-direction: column; flex: 1 1 auto; min-width: 0;
  background: #fff; border-radius: 6px; box-shadow: 0 1px 2px rgba(0,0,0,.06);
}
.pane-head { display: flex; align-items: center; gap: 4px; padding: 8px; }
.conv-list { flex: 1 1 auto; overflow-y: auto; }

.conv {
  position: relative; display: block; width: 100%; text-align: left;
  border: 0; border-bottom: 1px solid #eef0f4; background: transparent;
  padding: 8px 34px 8px 10px; font-size: 13px;
}
.conv:hover { background: #f6f7fb; }
.conv.active { background: #eef3ff; }
/* Over ten minutes waiting. Deliberately loud — the row a shift lead scans for. */
/*
 * Waiting past the threshold.
 *
 * A 3px stripe on an otherwise identical row was too quiet — the row still
 * read as ordinary at a glance, which is the one thing an overdue row must not
 * do. The whole row now carries it: a wider stripe, a red-tinted ground, and
 * the player's name in red rather than the same near-black every other row
 * uses. The name is what the eye lands on first, so leaving it neutral was
 * most of why the old treatment did not register.
 *
 * Kept to a tint rather than a saturated fill: several of these in a row have
 * to stay readable, and a queue of solid red blocks is a queue nobody can
 * scan.
 */
.conv.urgent { border-left: 4px solid #dc3545; background: #fff5f5; }
.conv.urgent .nm { color: #c92a2a; }
.conv.urgent:hover { background: #ffecec; }
/* The selected row keeps its own ground, or the red would fight the highlight
   and you could not tell which conversation is open. */
.conv.urgent.active { background: #ffe3e3; }
.conv .unread { position: absolute; top: 8px; right: 8px; }

/*
 * Colour by WHY a human was called in. A deposit question the bot could not
 * answer is money waiting, not an emergency: it gets its own teal and never
 * turns red, however long it waits (the wait is still printed). Withdrawal and
 * complaint get a tint too so the three money/anger reasons read apart, but
 * they still go red when overdue — only deposit was asked to stay out of red.
 */
.conv.reason-deposit, .conv.urgent.reason-deposit { border-left: 4px solid #0e8f85; background: #eefaf8; }
.conv.reason-deposit .nm, .conv.urgent.reason-deposit .nm { color: #0b6b64; }
.conv.reason-deposit:hover, .conv.urgent.reason-deposit:hover { background: #e3f5f2; }
.conv.reason-deposit.active, .conv.urgent.reason-deposit.active { background: #d8f0ec; }
.conv.urgent.reason-deposit .text-danger { color: #0b6b64 !important; }
.conv.reason-withdrawal:not(.urgent) { border-left: 4px solid #6f42c1; background: #f6f1fc; }
.conv.reason-complaint:not(.urgent) { border-left: 4px solid #b7791f; background: #fdf6e8; }

.reason-chip {
  display: inline-block; font-size: 10px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
  padding: 1px 7px; border-radius: 999px; background: #eef0f4; color: #5a6276; vertical-align: middle;
}
.rc-deposit    { background: #d7f2ee; color: #0b6b64; }
.rc-withdrawal { background: #e9ddf7; color: #5a2ea6; }
.rc-complaint  { background: #fdebd0; color: #8a5a00; }
.rc-faq_pass   { background: #e9ddf7; color: #5a2ea6; }
/* Tone chips: what the bot read in the customer's last line. */
.tc-hostile { background: #fde2e1; color: #a12622; }
.tc-praise  { background: #dff5e3; color: #1b6b35; }
.tc-lost    { background: #fff1d6; color: #8a5a00; }
.tc-unsure  { background: #e9ecf2; color: #4f5868; }
.tc-neutral { background: #eef0f4; color: #5a6276; }
.tone-row { display: flex; align-items: center; gap: 6px; margin-top: 3px; }
.tone-pick { font-size: 11px; border: 1px solid #dfe3ea; border-radius: 4px; background: #fff; color: #5a6276; padding: 0 4px; height: 20px; opacity: 0; transition: opacity .15s; }
.row-msg:hover .tone-pick, .tone-pick:focus { opacity: 1; }
.conv.reason-faq_pass:not(.urgent) { border-left: 4px solid #6f42c1; background: #f6f1fc; }
/* A brand-picked colour is set inline; keep the selected highlight visible on top of it. */
.conv.tinted.active { box-shadow: inset 0 0 0 2px rgba(37, 99, 235, .35); }

/* Deleted: the slot stays so the thread's shape is honest, the words do not. */
.row-msg .bub.deleted, .row-msg.right .bub.deleted { background: transparent !important; border: 1px dashed #d0d4df; color: #8a90a2 !important; font-style: italic; }
.msg-del {
  appearance: none; border: 0; background: transparent; color: #8a90a2;
  font-size: 11px; padding: 0; margin-top: 2px; cursor: pointer; opacity: 0;
}
.row-msg:hover .msg-del, .msg-del:focus-visible { opacity: 1; }
.msg-del:hover { color: #dc3545; }

.badge.st-waiting { background: #f0ad4e; color: #fff; }
.badge.st-human   { background: #2563eb; color: #fff; }
.badge.st-bot     { background: #adb5bd; color: #fff; }
.badge.st-resolved{ background: #6c757d; color: #fff; }

.thread-head {
  display: flex; align-items: center; flex-wrap: wrap; gap: 6px 8px;
  padding: 8px 10px; border-bottom: 1px solid #eef0f4;
}
/* Identity block: the name keeps at least a readable width, chips wrap under
   it. Before, everything sat on one line and "All brands" plus a reason chip
   left the name as "T…" and the Take over button three lines tall. */
.head-id { flex: 1 1 240px; }
.head-chips { gap: 4px 6px; }
.head-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
.head-chips .badge, .head-chips .reason-chip, .head-chips .brandtag { white-space: nowrap; }
.head-actions { display: flex; align-items: center; gap: 4px; flex: 0 0 auto; margin-left: auto; }
.head-actions .btn { white-space: nowrap; }
.head-assign { min-width: 180px; max-width: 240px; }
/*
 * The thread.
 *
 * ── Which side a message sits on ─────────────────────────────────────
 * Right is OUR side of the desk — agent replies and internal notes. Left is
 * everyone we are talking to: the player and the bot answering on our behalf.
 * The bot sits left on purpose: to the player it is not us, and an agent
 * scanning a handover needs to see at a glance what the player was already
 * told before a human arrived.
 *
 * Colour carries the same split, so the thread is readable at a glance with
 * the text out of focus: accent on the right, neutral on the left.
 */
.thread {
  flex: 1 1 auto; overflow-y: auto; padding: 16px 18px;
  display: flex; flex-direction: column; gap: 3px;
  /* A tinted ground, so a white player bubble is an object on a surface
     rather than invisible against the pane. */
  background: #f7f8fc;
}
.composer { border-top: 1px solid #eef0f4; padding: 10px 12px; background: #fff; }

.row-msg { display: flex; align-items: flex-end; gap: 8px; margin-top: 11px; }
/* Inside a run the gap closes up, so a burst reads as one turn. */
.row-msg.grouped { margin-top: 2px; }
.row-msg.right { flex-direction: row-reverse; }

/* One avatar per run. Hidden inside a run but still occupying its track, or
   the bubbles below it would slide out of column. */
.ava {
  flex: 0 0 28px; width: 28px; height: 28px; border-radius: 50%;
  display: grid; place-items: center; font-size: 11px; font-weight: 700;
  letter-spacing: .02em; user-select: none;
  background: #e4e7f2; color: #5a6276;
}
.ava.bot    { background: #ece6ff; color: #7C4DFF; font-size: 15px; }
.ava.agent,
.ava.note   { background: #7C4DFF; color: #fff; }
.ava[aria-hidden="true"] { background: transparent; }

.stack { min-width: 0; max-width: 72%; display: flex; flex-direction: column; }
.row-msg.right .stack { align-items: flex-end; }

.who { font-size: 11px; color: #8a90a2; margin-bottom: 3px; display: flex;
       align-items: center; gap: 6px; }
.who .nm { font-weight: 600; color: #5a6276; }
.who .ts { font-variant-numeric: tabular-nums; }
.tag-note {
  font-size: 10px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
  color: #9a6400; background: #fff1cf; padding: 1px 5px; border-radius: 3px;
}

.bub {
  padding: 9px 13px; border-radius: 14px; font-size: 14px; line-height: 1.5;
  white-space: pre-wrap; overflow-wrap: anywhere;
  background: #fff; color: #2b2f3a; border: 1px solid #e7eaf3;
}
/* The corner nearest the avatar is flattened, which is what makes a bubble
   point at its owner without drawing a tail. */
.row-msg.left  .bub { border-bottom-left-radius: 5px; }
.row-msg.right .bub { border-bottom-right-radius: 5px; }
.row-msg.grouped.left  .bub { border-top-left-radius: 5px; }
.row-msg.grouped.right .bub { border-top-right-radius: 5px; }

.row-msg.bot .bub   { background: #f2effd; border-color: #e3dbfb; }
.row-msg.agent .bub { background: #7C4DFF; border-color: #7C4DFF; color: #fff; }
/* A note was never sent. Dashed, so it does not read as a delivered reply
   sitting in the player's chat. */
.row-msg.note .bub  { background: #fffaf0; border: 1px dashed #e9c26a; color: #6b4e12; }

/* Brand attribution, shown only when the list is mixed across tenants. */
.brandline { font-size: 11.5px; color: #7C4DFF; margin-top: 2px; }
.brandline .mdi { font-size: 12px; }
.brandtag {
  font-size: 11px; font-weight: 600; color: #7C4DFF; background: #f0ebff;
  border: 1px solid #e0d6ff; padding: 1px 7px; border-radius: 4px; white-space: nowrap;
}

/*
 * "New since you last looked".
 *
 * A rule across the full width with the label sitting on it — the same shape
 * every mail client and Slack use, because it has to read as a boundary in the
 * transcript rather than as another message.
 */
.unread-line {
  display: flex; align-items: center; gap: 10px;
  margin: 16px 0 4px; color: #dc3545;
  font-size: 11.5px; font-weight: 700; letter-spacing: .04em;
}
.unread-line::before, .unread-line::after {
  content: ''; flex: 1 1 auto; height: 1px; background: currentColor; opacity: .35;
}

.sysline { display: flex; justify-content: center; margin: 12px 0 2px; }
.sysline span {
  font-size: 11.5px; color: #8a90a2; background: #eceef6;
  padding: 3px 11px; border-radius: 999px;
}

.undelivered { font-size: 11px; color: #e0566a; margin-top: 3px; }

.att { margin-top: 7px; }
.att img { max-width: 220px; max-height: 220px; border-radius: 9px; display: block; }
.att .file { display: inline-flex; align-items: center; gap: 5px; font-size: 13px; }
.row-msg.agent .att .file { color: #fff; text-decoration: underline; }

.inbox-error { position: fixed; bottom: 12px; left: 50%; transform: translateX(-50%); z-index: 1050; }

/* ── Mobile ──────────────────────────────────────────────────────────
   One pane at a time. A night-shift agent is on a phone, and two columns
   at 390px wide is two unusable columns. */
@media (max-width: 767.98px) {
  .inbox { height: calc(100vh - 110px); gap: 0; }
  .pane-list { width: 100%; flex: 1 1 auto; }
  .thread { padding: 12px 10px; }
  .stack { max-width: 82%; }
}
/* ── Player card (Member tab) ───────────────────────────────────────────── */
.player-card { font-size: 12.5px; }
.pc-head { margin-bottom: 8px; }
.pc-name { font-weight: 700; font-size: 14px; color: #2b2f3a; }
.pc-user { font-weight: 500; color: #6c757d; }
.pc-badges { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
.pc-badge { font-size: 10px; font-weight: 700; letter-spacing: .04em; border-radius: 9px; padding: 1px 7px; background: #eef1f7; color: #5c6478; }
.pc-badge.ok { background: #e4f6ea; color: #1e7a3f; }
.pc-badge.warn { background: #fdecec; color: #a8322c; }
.pc-badge.tag { background: #f1eefc; color: #5a2ea6; }
.pc-tiles { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 10px; }
.pc-tile { border-radius: 8px; padding: 8px 10px; background: #e9f7ee; border: 1px solid #cdebd8; }
.pc-tile.alt { background: #eef3ff; border-color: #d6e0fb; }
.pc-tile-l { font-size: 10.5px; color: #6c757d; }
.pc-tile-v { font-size: 15px; font-weight: 700; color: #1e7a3f; font-variant-numeric: tabular-nums; }
.pc-tile.alt .pc-tile-v { color: #2f55d4; }
.pc-tile-s { font-size: 10.5px; color: #8a93aa; }
.pc-sec { border: 1px solid #e3e6ed; border-radius: 8px; padding: 6px 10px; margin-bottom: 8px; background: #fff; }
.pc-sec.bank { border-color: #f3dfb8; background: #fffdf7; }
.pc-sec-t { font-size: 10px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #5a2ea6; margin-bottom: 2px; }
.pc-sec.bank .pc-sec-t { color: #b7791f; }
.pc-row { display: flex; justify-content: space-between; gap: 10px; padding: 4px 0; border-top: 1px solid #f1f2f6; }
.pc-row:first-of-type { border-top: 0; }
.pc-row span { color: #6c757d; flex: none; }
.pc-row b { font-weight: 600; color: #2b2f3a; text-align: right; overflow-wrap: anywhere; }
.pc-row .mono { font-family: SFMono-Regular, Menlo, Consolas, monospace; font-size: 11.5px; }
.pc-link { font-size: 11px; }
.pc-foot { font-size: 10.5px; color: #8a93aa; text-align: center; margin: 4px 0 6px; }

</style>
